import React, { useState, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { makeStyles } from '@material-ui/core/styles';
import InputBase from '@material-ui/core/InputBase';
import AccountBalance from '@material-ui/icons/AccountBalance';
import Search from '@material-ui/icons/Search';
import Close from '@material-ui/icons/Close';
import ExitToApp from '@material-ui/icons/ExitToApp';

import auth from '../../services/auth';
import {
  primaryColor,
  grayColor,
  dangerColor,
  whiteColor,
  blackColor,
  hexToRgb
} from 'assets/jss/material-dashboard-pro-react.js';

const useStyles = makeStyles({
  panel: {
    width: '360px',
    maxWidth: '92vw',
    background: whiteColor,
    borderRadius: '14px',
    overflow: 'hidden',
    boxShadow: '0 12px 40px 0 rgba(' + hexToRgb(blackColor) + ', 0.18)'
  },
  header: {
    background:
      'linear-gradient(135deg, ' +
      primaryColor[4] +
      ' 0%, ' +
      primaryColor[1] +
      ' 100%)',
    padding: '18px 18px 20px',
    color: whiteColor
  },
  headerTop: {
    display: 'flex',
    alignItems: 'center'
  },
  avatar: {
    width: '44px',
    height: '44px',
    borderRadius: '50%',
    background: 'rgba(' + hexToRgb(whiteColor) + ', 0.25)',
    color: whiteColor,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontWeight: 700,
    fontSize: '16px',
    marginRight: '12px',
    flexShrink: 0
  },
  headerInfo: {
    display: 'flex',
    flexDirection: 'column',
    flex: 1,
    minWidth: 0
  },
  headerLabel: {
    fontSize: '11px',
    fontWeight: 600,
    letterSpacing: '0.5px',
    textTransform: 'uppercase',
    opacity: 0.85
  },
  headerTitle: {
    fontSize: '18px',
    fontWeight: 700,
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis'
  },
  closeBtn: {
    border: 'none',
    background: 'rgba(' + hexToRgb(whiteColor) + ', 0.2)',
    color: whiteColor,
    width: '30px',
    height: '30px',
    borderRadius: '8px',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
    '& svg': { fontSize: '18px' },
    '&:hover': { background: 'rgba(' + hexToRgb(whiteColor) + ', 0.32)' }
  },
  body: {
    padding: '16px 18px 8px'
  },
  sectionRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: '10px'
  },
  sectionLabel: {
    fontSize: '12px',
    fontWeight: 600,
    letterSpacing: '0.5px',
    textTransform: 'uppercase',
    color: grayColor[0]
  },
  count: {
    fontSize: '12px',
    fontWeight: 600,
    color: grayColor[1]
  },
  searchBox: {
    display: 'flex',
    alignItems: 'center',
    background: grayColor[13],
    borderRadius: '10px',
    padding: '0 12px',
    marginBottom: '12px'
  },
  searchIcon: {
    color: grayColor[0],
    fontSize: '20px',
    marginRight: '8px'
  },
  searchInput: {
    flex: 1,
    fontSize: '14px',
    '& input': { padding: '10px 0' }
  },
  list: {
    maxHeight: '220px',
    overflowY: 'auto',
    margin: '0 -4px',
    padding: '0 4px'
  },
  item: {
    display: 'flex',
    alignItems: 'center',
    width: '100%',
    border: 'none',
    background: 'transparent',
    borderRadius: '10px',
    padding: '10px 12px',
    marginBottom: '4px',
    cursor: 'pointer',
    textAlign: 'left',
    transition: 'background .2s ease',
    '&:hover': { background: grayColor[13] }
  },
  itemAtiva: {
    background: 'rgba(' + hexToRgb(primaryColor[0]) + ', 0.08)',
    cursor: 'default'
  },
  itemIcon: {
    width: '34px',
    height: '34px',
    borderRadius: '8px',
    background: grayColor[13],
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    marginRight: '12px',
    flexShrink: 0,
    '& svg': { fontSize: '18px', color: grayColor[1] }
  },
  itemText: {
    flex: 1,
    fontSize: '14px',
    fontWeight: 500,
    color: grayColor[2],
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis'
  },
  itemBadge: {
    fontSize: '10px',
    fontWeight: 700,
    textTransform: 'uppercase',
    color: primaryColor[0],
    border: '1px solid ' + primaryColor[0],
    borderRadius: '12px',
    padding: '2px 8px',
    flexShrink: 0
  },
  empty: {
    textAlign: 'center',
    color: grayColor[0],
    fontSize: '13px',
    padding: '16px 0'
  },
  logout: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: 'calc(100% - 36px)',
    margin: '8px 18px 16px',
    border: 'none',
    background: 'rgba(' + hexToRgb(dangerColor[0]) + ', 0.1)',
    color: dangerColor[0],
    borderRadius: '10px',
    padding: '12px',
    fontSize: '14px',
    fontWeight: 600,
    cursor: 'pointer',
    transition: 'background .2s ease',
    '&:hover': { background: 'rgba(' + hexToRgb(dangerColor[0]) + ', 0.18)' }
  },
  logoutIcon: {
    fontSize: '20px',
    marginRight: '8px'
  }
});

export default function Unidade({ onClose, onLogout }) {
  const classes = useStyles();
  const [unidades, setUnidades] = useState([]);
  const [ativa, setAtiva] = useState('');
  const [busca, setBusca] = useState('');

  const carregar = useCallback(async () => {
    const chave = localStorage.getItem('lukos-token-fw');
    try {
      const [lista, sel] = await Promise.all([
        auth.post('/faturaweb/uniddisppost', { params: { chave } }),
        auth.post('/faturaweb/unidselpost', { params: { chave } })
      ]);
      if (lista.status === 200) setUnidades(lista.data || []);
      if (sel.status === 200) setAtiva(sel.data?.unidOpe || '');
    } catch (error) {
      console.error('Erro ao carregar unidades:', error);
    }
  }, []);

  useEffect(() => {
    carregar();
  }, [carregar]);

  const setaUnidade = async (id) => {
    const chave = localStorage.getItem('lukos-token-fw');
    try {
      const response = await auth.post(`/faturaweb/setaunid/'${id}`, {
        params: { chave }
      });
      if (response.status === 200) {
        localStorage.setItem('lukos-token-fw', response.data.token);
        if (onClose) onClose();
        // Recarrega para refletir a nova unidade em todos os dados
        window.location.href = '/';
      }
    } catch (error) {
      console.error('Erro ao trocar unidade:', error);
    }
  };

  const termo = busca.trim().toLowerCase();
  const filtradas = termo
    ? unidades.filter((u) => (u.unidOpe || '').toLowerCase().includes(termo))
    : unidades;

  const iniciais = (ativa || 'UN').trim().substring(0, 2).toUpperCase();

  return (
    <div className={classes.panel}>
      <div className={classes.header}>
        <div className={classes.headerTop}>
          <div className={classes.avatar}>{iniciais}</div>
          <div className={classes.headerInfo}>
            <span className={classes.headerLabel}>Unidade ativa</span>
            <span className={classes.headerTitle}>{ativa || '—'}</span>
          </div>
          {onClose && (
            <button
              type="button"
              className={classes.closeBtn}
              onClick={onClose}
              aria-label="Fechar"
            >
              <Close />
            </button>
          )}
        </div>
      </div>

      <div className={classes.body}>
        <div className={classes.sectionRow}>
          <span className={classes.sectionLabel}>Trocar unidade</span>
          <span className={classes.count}>{unidades.length}</span>
        </div>

        <div className={classes.searchBox}>
          <Search className={classes.searchIcon} />
          <InputBase
            placeholder="Procurar unidade…"
            value={busca}
            onChange={(e) => setBusca(e.target.value)}
            className={classes.searchInput}
          />
        </div>

        <div className={classes.list}>
          {filtradas.length === 0 && (
            <div className={classes.empty}>Nenhuma unidade encontrada</div>
          )}
          {filtradas.map((u) => {
            const isAtiva = u.unidOpe === ativa;
            return (
              <button
                type="button"
                key={u.idUnidOPeVinculo}
                className={classNames(classes.item, {
                  [classes.itemAtiva]: isAtiva
                })}
                onClick={() => !isAtiva && setaUnidade(u.idUnidOPeVinculo)}
              >
                <span className={classes.itemIcon}>
                  <AccountBalance />
                </span>
                <span className={classes.itemText}>{u.unidOpe}</span>
                {isAtiva && <span className={classes.itemBadge}>Ativa</span>}
              </button>
            );
          })}
        </div>
      </div>

      <button type="button" className={classes.logout} onClick={onLogout}>
        <ExitToApp className={classes.logoutIcon} />
        Sair do sistema
      </button>
    </div>
  );
}

Unidade.propTypes = {
  onClose: PropTypes.func,
  onLogout: PropTypes.func
};
